'use client';

import { useEffect, useState } from 'react';
import { RefreshCw, Home, AlertTriangle, ChevronDown, ChevronUp, Copy, CheckCircle2 } from 'lucide-react';

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    console.error('Page error:', error);
  }, [error]);

  const handleCopy = async () => {
    // Include the stack — without it the bug report is just "something went
    // wrong somewhere." The minified URLs in the stack tell us which chunk
    // threw, and the top few frame offsets narrow it down further.
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '(unknown UA)';
    const text = [
      `Error: ${error.name}: ${error.message}`,
      error.digest ? `Digest: ${error.digest}` : '',
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `UA: ${ua}`,
      '',
      'Stack:',
      error.stack ?? '(no stack)',
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center max-w-lg">
        <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-6">
          <AlertTriangle size={28} className="text-amber-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-3">Something went wrong</h1>
        <p className="text-fg-muted text-sm leading-relaxed mb-6">
          This page ran into an unexpected error. Your data is safe — try refreshing, or head back to the dashboard.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <RefreshCw size={14} />
            Try Again
          </button>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-card-bg hover:bg-card-hover-bg border border-default text-slate-300 text-sm font-medium rounded-lg transition-colors"
          >
            <Home size={14} />
            Dashboard
          </a>
        </div>

        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 text-[11px] text-fg-subtle hover:text-fg transition-colors font-mono"
          >
            {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Copy size={12} />}
            {copied ? 'Copied!' : 'Copy error code'}
          </button>
          <span className="text-slate-700">|</span>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="inline-flex items-center gap-1.5 text-[11px] text-fg-subtle hover:text-fg transition-colors"
          >
            {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {showDetails ? 'Hide details' : 'Show details'}
          </button>
        </div>

        {showDetails && (
          <div className="mt-3 text-left bg-surface border border-default rounded-lg p-4 overflow-auto max-h-48">
            <p className="text-xs text-red-400 font-semibold mb-1">{error.name}: {error.message}</p>
            {error.stack && (
              <pre className="text-[10px] text-fg-subtle whitespace-pre-wrap break-words font-mono leading-relaxed">
                {error.stack}
              </pre>
            )}
            {error.digest && (
              <p className="text-[10px] text-slate-600 mt-2 font-mono">Digest: {error.digest}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
